import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, dateFmt, money } from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { Plus, Search } from "lucide-react";

export default function Services() {
    const [list, setList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/services", { params: statusFilter ? { status: statusFilter } : {} });
                if (data) setList(data);
            } catch (e) {
                console.warn("services fetch failed", e);
            } finally {
                setLoading(false);
            }
        })();
    }, [statusFilter]);

    const filtered = list.filter((s) => {
        if (!q) return true;
        const qq = q.toLowerCase();
        return (
            s.folio?.toLowerCase().includes(qq) ||
            s.patient_name?.toLowerCase().includes(qq) ||
            s.client_name?.toLowerCase().includes(qq)
        );
    });

    return (
        <div className="space-y-4" data-testid="services-page">
            <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1">
                    <div className="flex items-center gap-2 px-3 py-1.5 border border-slate-200 rounded-sm bg-white max-w-md flex-1">
                        <Search className="w-3.5 h-3.5 text-slate-400" strokeWidth={1.5} />
                        <input
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Buscar folio, paciente, cliente..."
                            className="bg-transparent outline-none text-sm flex-1"
                            data-testid="services-search"
                        />
                    </div>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="fm-input max-w-xs" data-testid="services-status-filter">
                        <option value="">Todos los estatus</option>
                        <option value="solicitud_recibida">Solicitudes recibidas</option>
                        <option value="autorizado">Autorizados</option>
                        <option value="unidad_asignada">Unidad asignada</option>
                        <option value="en_ruta_destino">En ruta</option>
                        <option value="servicio_finalizado">Finalizados</option>
                        <option value="facturado">Facturados</option>
                        <option value="cobrado">Cobrados</option>
                        <option value="cancelado">Cancelados</option>
                    </select>
                </div>
                <Link to="/services/new" className="fm-btn-accent">
                    <Plus className="w-3.5 h-3.5" strokeWidth={2} /> Nueva solicitud
                </Link>
            </div>

            <div className="fm-card overflow-hidden">
                {loading ? (
                    <div className="p-6 text-sm text-slate-500 font-mono">Cargando servicios...</div>
                ) : filtered.length === 0 ? (
                    <div className="p-10 text-center text-sm text-slate-500">Sin servicios. <Link to="/services/new" className="text-slate-900 font-bold underline">Crear el primero</Link></div>
                ) : (
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                {["Folio", "Cliente", "Paciente", "Tipo", "Urgencia", "Unidad", "Total", "Estatus", "Fecha"].map((h) => (
                                    <th key={h} className="text-left py-2.5 px-3 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((s) => (
                                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50">
                                    <td className="py-2 px-3 font-mono text-xs font-bold">
                                        <Link to={`/services/${s.id}`} className="text-slate-900 hover:text-red-600" data-testid={`service-row-${s.folio}`}>{s.folio}</Link>
                                    </td>
                                    <td className="py-2 px-3 text-slate-700">{s.client_name}</td>
                                    <td className="py-2 px-3 text-slate-900 font-medium">{s.patient_name}</td>
                                    <td className="py-2 px-3 text-slate-700"><span className="font-mono text-xs">{s.service_type_code}</span> · {s.service_type_name}</td>
                                    <td className="py-2 px-3"><UrgencyBadge urgency={s.urgency_level} /></td>
                                    <td className="py-2 px-3 font-mono text-xs">{s.unit_number || "—"}</td>
                                    <td className="py-2 px-3 font-mono text-xs font-bold tabular-nums text-right">{money(s.total)}</td>
                                    <td className="py-2 px-3"><StatusBadge status={s.status} /></td>
                                    <td className="py-2 px-3 text-xs font-mono text-slate-500">{dateFmt(s.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
